import { useLayoutEffect, useRef, useEffect, useCallback, useState } from 'react';
import classnames from 'classnames';
import { useToggle } from 'react-use';
import qs from 'qs';
import download from 'downloadjs';
import useSound from 'use-sound';
import { gsap } from 'gsap';
import { isChrome } from 'react-device-detect';

import Intro from 'components/organisms/Intro';
import Toolbar from 'components/molecules/Toolbar';
import HighlightSlider from 'components/molecules/HighlightSlider';
import Menu from 'components/molecules/Menu';
import Icon from 'components/atoms/Icon';
import Transition from 'components/atoms/Transition';
import InfoDetail from 'components/molecules/InfoDetail';
import ImageSlider from 'components/molecules/ImageSlider';
import Video from 'components/atoms/Video';
import Audio from 'components/molecules/Audio';
import ShareModal from 'components/molecules/ShareModal';

import { getImageSrc, getAudioSrc } from 'utils/content';
import { useHasNotch, useMedia, useWindowSize } from 'utils/hooks';

import tag from 'images/icons/tag.png';
import video from 'images/icons/video.png';
import audio from 'images/icons/audio.png';
import menuIcon from 'images/icons/menu.png';
import closeIcon from 'images/icons/close.png';

import defaultMusic from 'audio/test.mp3';

import type { Tool, View } from 'components/molecules/Toolbar';
import type { Intro as MenuIntro } from 'components/molecules/Menu';
import type { TourData, ImageAlign } from 'utils/types/customization';

import type {
    ShowcaseEmbedWindow,
    MpSdk,
    Dictionary,
    Tour,
    Vector2,
    Vector3,
} from '../../../../types/matterport-sdk';

import styles from './index.css';

const { MATTERPORT_SDK_KEY } = process.env;

const VIEW_MODE_MAP: Record<View, keyof MpSdk['Mode']['Mode']> = {
    walking: 'INSIDE',
    dollhouse: 'DOLLHOUSE',
    floorPlan: 'FLOORPLAN',
};
export interface MatterportShowcaseProp {
    className?: string;
    initTour: string;
    initPos?: {
        s: string;
        x: number;
        y: number;
    };
    onClickTour?: (tourData: TourData) => void;

    logo?: string;

    // tools
    info?: boolean;
    infoDefault?: boolean;
    position?: boolean;
    positionDefault?: boolean;
    highlight?: boolean;
    tour?: boolean;
    view?: boolean;
    floors?: boolean;
    music?: boolean;
    share?: boolean;
    screenshot?: boolean;
    getLocation?: boolean;

    // menu
    menu?: boolean;
    intro?: MenuIntro[];
    tourList?: string[];
    tourData?: { [key: string]: TourData };
    fixedLink?: {
        text: string;
        url: string;
    };

    // music
    musicSrc?: string;

    // images config
    imageAlignDefault?: ImageAlign;
    imageAlignExceptions?: { [key: string]: ImageAlign };

    isStorybook?: boolean;
}

const MatterportShowcase: React.FC<MatterportShowcaseProp> = ({
    className,
    initTour,
    initPos,
    onClickTour: onClickTourFn = () => {},

    // tools
    info: toolInfo = true,
    infoDefault = true,
    position: toolPosition = true,
    positionDefault = true,
    highlight: toolHighlight = true,
    tour: toolTour = true,
    view: toolView = true,
    floors: toolFloors = true,
    music: toolMusic = true,
    share: toolShare = true,
    screenshot: toolScreenshot = false,
    getLocation: toolGetLocation = false,

    logo,

    // menu
    menu = true,
    intro,
    tourList = [],
    tourData = {},
    fixedLink,

    // music
    musicSrc,

    // images config
    imageAlignDefault = 'center',
    imageAlignExceptions = {},

    isStorybook,
}) => {
    const iframe = useRef<HTMLIFrameElement>(null);
    const mpSdk = useRef<MpSdk | null>(null);

    const [activeTour, setActiveTour] = useState(initTour);

    const getEmbedQueryStr = useCallback(
        ({ modelId, sr, ss }: { modelId: string; sr?: string; ss?: string }) => {
            // doc: https://support.matterport.com/s/article/URL-Parameters
            // measurement mode 要手動關
            return qs.stringify({
                applicationKey: MATTERPORT_SDK_KEY,
                m: modelId,
                play: 1,
                help: 0,
                qs: 1,
                brand: 0,
                gt: 0,
                hr: 0,
                mls: 2,
                search: 0,
                title: 0,
                tourcta: 0,
                lang: 'en',
                sr,
                ss,
            });
        },
        [],
    );
    const [embededQueryStr, setEmbededQueryStr] = useState(
        getEmbedQueryStr({
            modelId: tourData[initTour].modelId,
            sr: tourData[initTour].sr,
            ss: tourData[initTour].ss,
        }),
    );

    const [initPosLoaded, setInitPosLoaded] = useState(false);
    const initPosLoadTimeout = useRef<number | null>(null);
    const setInitPos = useCallback(() => {
        if (mpSdk.current && initPos && !initPosLoaded) {
            if (initPosLoadTimeout.current) {
                window.clearTimeout(initPosLoadTimeout.current);
            }

            initPosLoadTimeout.current = window.setTimeout(() => {
                const { s, x, y } = initPos;
                mpSdk.current?.Sweep.moveTo(s, {
                    rotation: { x, y },
                    transition: mpSdk.current?.Sweep.Transition.INSTANT,
                });
                setInitPosLoaded(true);
                initPosLoadTimeout.current = null;
            }, 500);
        }
    }, [mpSdk.current, initPos, initPosLoaded]);
    useEffect(() => {
        if (initPosLoadTimeout.current) {
            window.clearTimeout(initPosLoadTimeout.current);
        }
    }, []);

    const tagsCollection = useRef<Dictionary<MpSdk.Mattertag.ObservableMattertagData>>({});
    const [floorsData, updateFloorsData] = useState<{ label: string; value: number }[]>([]);

    const { width, height } = useWindowSize();

    const prevWidthRef = useRef<number>(width);
    const prevHeightRef = useRef<number>(height);
    const nowWidthRef = useRef<number>(width);
    const nowHeightRef = useRef<number>(height);

    const [startIntroActive, setStartIntroActive] = useState(true);
    const [appPhase, setAppPhase] = useState<MpSdk.App.Phase | undefined>();

    // toolbar
    const [toolbarInfoActive, toggleToolbarInfo] = useToggle(infoDefault);
    const [toolbarPostiionActive, toggleToolbarPostion] = useToggle(positionDefault);
    const [toolbarHighlightsOpen, toggleToolbarHighlights] = useToggle(false);
    const [toolbarTourPlaying, toggleToolbarTour] = useToggle(false);
    const toolbarTourPlayingRef = useRef(false);
    const [toolbarMusicActive, toggleToolbarMusic] = useToggle(false);

    const [isInside, toggleIsInside] = useToggle(false);
    const isInsideRef = useRef(true);

    const prevMusicActive = useRef(false);
    const [highlightData, setHighlightData] = useState<Tour.Snapshot[]>([]);

    // side content
    const [menuActive, toggleMenu] = useToggle(false);

    // info
    const [infoDetailActive, toggleInfoDetail] = useToggle(false);
    const [infoLabel, setInfoLabel] = useState('');
    const [infoDescription, setInfoDescription] = useState('');
    const [infoImages, setInfoImages] = useState<string[]>([]);
    const [infoImageType, setInfoImageType] = useState<'default' | 'zoomable'>('default');
    const [infoImageAlign, setInfoImageAlign] = useState<ImageAlign>(imageAlignDefault);
    const [imageSlideActive, toggleImageSlide] = useToggle(false);
    const userSweep = useRef<string>('');
    const userPosition = useRef<Vector3 | null>(null);
    const userRotation = useRef<Vector2 | null>(null);

    // video
    const [videoActive, toggleVideo] = useToggle(false);
    const [videoSrc, setVideoSrc] = useState('');

    // audio
    const [audioActive, toggleAudio] = useToggle(false);
    const [audioOpen, toggleAudioOpen] = useToggle(false);
    const [audioLabel, setAudioLabel] = useState('');
    const [audioSrc, setAudioSrc] = useState('');

    // share
    const [shareModalActive, toggleShareModal] = useToggle(false);
    const [shareUrl, setShareUrl] = useState('');

    const sideUIOpening = useRef(false);
    const sideUIOpeningTimeout = useRef<number | null>(null);
    const moving = useRef(false);
    const checkMovingTimeout = useRef<number | null>(null);
    const openDetailCheckTimeout = useRef<number | null>(null);
    const openDetailTimeout = useRef<number | null>(null);

    const translating = useRef(false);

    const [playingMusicSrc, togglePlayingMusicSrc] = useToggle(false);

    const [playMusic, { pause: pauseMusic }] = useSound(musicSrc || defaultMusic, {
        html5: true,
        loop: true,
        onload: () => {
            if (!startIntroActive) {
                togglePlayingMusicSrc(true);
                toggleToolbarMusic(true);
                prevMusicActive.current = true;
            }
        },
    });

    useEffect(() => {
        if (playingMusicSrc) {
            playMusic();
        } else {
            pauseMusic();
        }
    }, [playingMusicSrc, playMusic, pauseMusic]);

    const onEnterMain = useCallback(() => {
        setStartIntroActive(false);
    }, []);

    const connectSdk = useCallback(async () => {
        if (iframe.current) {
            const showcaseWindow = iframe.current.contentWindow as ShowcaseEmbedWindow;

            mpSdk.current = await showcaseWindow.MP_SDK.connect(
                // @ts-ignore: next-line
                showcaseWindow,
                MATTERPORT_SDK_KEY as string,
                '',
            );

            const stylesheet = document.createElement('link');
            stylesheet.rel = 'stylesheet';
            stylesheet.type = 'text/css';
            stylesheet.href = '/bundle/showcase.css';
            iframe.current.contentDocument?.getElementsByTagName('head')[0].appendChild(stylesheet);
        }
    }, []);

    const registerIcon = useCallback(async () => {
        await mpSdk.current?.Asset.registerTexture('default', `${isStorybook ? '/' : ''}${tag}`);
        await mpSdk.current?.Asset.registerTexture('audio', `${isStorybook ? '/' : ''}${audio}`);
        await mpSdk.current?.Asset.registerTexture('video', `${isStorybook ? '/' : ''}${video}`);
    }, []);

    const handleModelState = useCallback(() => {
        mpSdk.current?.App.state.subscribe((appState) => {
            setAppPhase(appState.phase);
        });
    }, []);

    const handlePose = useCallback(() => {
        mpSdk.current?.Camera.pose.subscribe((pose) => {
            userSweep.current = pose.sweep;
            userPosition.current = pose.position;
            userRotation.current = pose.rotation;
        });
    }, []);

    const openDetail = useCallback(() => {
        if (moving.current) {
            openDetailCheckTimeout.current = window.setTimeout(() => {
                openDetail();
                openDetailCheckTimeout.current = null;
            }, 100);
        } else {
            toggleInfoDetail(true);
        }
    }, []);

    const onClickTag = useCallback((tagData: MpSdk.Mattertag.ObservableMattertagData) => {
        const {
            sid,
            media: { src: mediaSrc, type: mediaType },
            label,
            description,
        } = tagData;

        if (isInsideRef.current) {
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.log('tag', sid);
            }

            // video
            if (mediaType === mpSdk.current?.Mattertag.MediaType.VIDEO) {
                setVideoSrc(mediaSrc);
                toggleVideo(true);

                toggleToolbarHighlights(false);
                toggleAudio(false);
                toggleInfoDetail(false);
                toggleMenu(false);
            }

            // audio
            else if (description.match(/\[.*?語音導覽.*?\]\((.*?)\)/)) {
                toggleToolbarHighlights(false);
                toggleInfoDetail(false);
                toggleMenu(false);

                sideUIOpening.current = true;

                if (sideUIOpeningTimeout.current) {
                    window.clearTimeout(sideUIOpeningTimeout.current);
                }

                sideUIOpeningTimeout.current = window.setTimeout(() => {
                    sideUIOpening.current = false;
                    sideUIOpeningTimeout.current = null;
                }, 500);

                const audioUrl = getAudioSrc(description);
                setAudioLabel(label);
                setAudioSrc(audioUrl);
                toggleAudio(true);
                toggleAudioOpen(true);
            }

            // images or text
            else {
                toggleToolbarHighlights(false);
                toggleAudio(false);
                toggleInfoDetail(false);
                toggleMenu(false);

                sideUIOpening.current = true;

                if (sideUIOpeningTimeout.current) {
                    window.clearTimeout(sideUIOpeningTimeout.current);
                }

                sideUIOpeningTimeout.current = window.setTimeout(() => {
                    sideUIOpening.current = false;
                    sideUIOpeningTimeout.current = null;
                }, 500);

                if (mediaType === mpSdk.current?.Mattertag.MediaType.PHOTO) {
                    setInfoImages([mediaSrc]);
                    setInfoImageType('default');
                    setInfoImageAlign(imageAlignExceptions[sid] || imageAlignDefault);
                } else {
                    const images = getImageSrc(description);
                    setInfoImages(images);
                    setInfoImageAlign(imageAlignExceptions[sid] || imageAlignDefault);

                    if (images.length > 0) {
                        setInfoImageType('zoomable');
                    }
                }

                setInfoLabel(label);
                setInfoDescription(description);

                openDetailTimeout.current = window.setTimeout(() => {
                    openDetail();
                    openDetailTimeout.current = null;
                }, 500);
            }
        }
    }, []);

    useEffect(() => {
        if (!startIntroActive && musicSrc && prevMusicActive.current) {
            if (videoActive || audioActive) {
                togglePlayingMusicSrc(false);
            } else {
                togglePlayingMusicSrc(true);
            }
        }
    }, [startIntroActive, musicSrc, videoActive, audioActive]);

    const blurSideUI = useCallback((isToolbar = false) => {
        toggleMenu(false);

        if (!sideUIOpening.current) {
            toggleInfoDetail(false);
            toggleAudio(false);
        }

        if (!isToolbar && toolbarTourPlayingRef.current) {
            toggleToolbarTour(false);
        }
    }, []);

    const handleViewChange = useCallback(() => {
        mpSdk.current?.on(mpSdk.current.Mode.Event.CHANGE_END, (lastMode) => {
            toggleIsInside(lastMode === 'mode.inside' || lastMode === 'mode.outside');
        });
    }, []);

    const handleMatterTag = useCallback(() => {
        mpSdk.current?.Mattertag.data.subscribe({
            onAdded: (id, item) => {
                const {
                    media: { type: mediaType },
                    description,
                } = item;

                if (mediaType === mpSdk.current?.Mattertag.MediaType.VIDEO) {
                    mpSdk.current?.Mattertag.editIcon(id, 'video');
                } else if (description.match(/\[.*?語音導覽.*?\]\((.*?)\)/)) {
                    mpSdk.current?.Mattertag.editIcon(id, 'audio');
                } else {
                    mpSdk.current?.Mattertag.editIcon(id, 'default');
                }

                // prevent open default description
                mpSdk.current?.Tag.allowAction(id, {
                    opening: false,
                    navigating: false,
                });
                mpSdk.current?.Mattertag.editColor(id, { r: 1, g: 1, b: 1 });
                mpSdk.current?.Mattertag.editOpacity(id, 1);
            },
            onCollectionUpdated: (collection) => {
                tagsCollection.current = collection;
            },
        });

        mpSdk.current?.on(mpSdk.current.Mattertag.Event.CLICK, (id) => {
            if (id) {
                onClickTag(tagsCollection.current[id]);
            }
        });

        mpSdk.current?.on(mpSdk.current.Camera.Event.MOVE, () => {
            if (
                prevWidthRef.current === nowWidthRef.current &&
                prevHeightRef.current === nowHeightRef.current
            ) {
                if (checkMovingTimeout.current) {
                    window.clearTimeout(checkMovingTimeout.current);
                }

                moving.current = true;
                checkMovingTimeout.current = window.setTimeout(() => {
                    moving.current = false;
                    checkMovingTimeout.current = null;
                }, 300);
            } else {
                prevWidthRef.current = nowWidthRef.current;
                prevHeightRef.current = nowHeightRef.current;
            }
        });
    }, []);

    const handleFloors = useCallback(() => {
        mpSdk.current?.Floor.data.subscribe({
            onCollectionUpdated: (collection) => {
                const floorKeys = Object.keys(collection).sort(
                    (a, b) => parseInt(a, 10) - parseInt(b, 10),
                );
                const nextFloorData = floorKeys.map((sequence) => ({
                    label: collection[sequence].name,
                    value: parseInt(sequence, 10) + 1,
                }));
                updateFloorsData(nextFloorData);
            },
        });
    }, []);

    const getTourData = useCallback(async () => {
        const snapshots = await mpSdk.current?.Tour.getData();

        setHighlightData(snapshots || []);
    }, []);

    const onClickHighlight = useCallback(async (index: number) => {
        if (toolbarTourPlayingRef.current) {
            await mpSdk.current?.Tour.stop();
            toggleToolbarTour(false);
        }
        if (!translating.current) {
            translating.current = true;

            try {
                await mpSdk.current?.Tour.step(index);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.log(err);
            }

            translating.current = false;
        }
    }, []);

    const changeView = useCallback(async (nextView: View) => {
        if (toolbarTourPlayingRef.current) {
            await mpSdk.current?.Tour.stop();
            toggleToolbarTour(false);
        }

        mpSdk.current?.Mode.moveTo(mpSdk.current?.Mode.Mode[VIEW_MODE_MAP[nextView]]);
    }, []);

    const changeFloor = useCallback(async (nextFloor) => {
        if (toolbarTourPlayingRef.current) {
            await mpSdk.current?.Tour.stop();
            toggleToolbarTour(false);
        }
        if (nextFloor === 0) {
            mpSdk.current?.Floor.showAll();
        } else {
            mpSdk.current?.Floor.moveTo(nextFloor - 1);
        }
    }, []);

    const logSweepData = useCallback(() => {
        /* eslint-disable no-console */
        console.log({
            sweepId: userSweep.current,
            position: userPosition.current,
            rotation: userRotation.current,
        });
        /* eslint-enable no-console */
    }, []);

    const setPosShareUrl = useCallback(async () => {
        const query = qs.stringify({
            s: userSweep.current,
            x: userRotation.current ? userRotation.current.x : '',
            y: userRotation.current ? userRotation.current.y : '',
        });

        setShareUrl(
            `${window.location.href.split('?')[0]}${
                window.location.href.split('?')[0].endsWith('/') ? '' : '/'
            }?${query}`,
        );
    }, []);

    const takeScreenShot = useCallback(async () => {
        const resolution = {
            width: 1920,
            height: 1080,
        };

        const visibility = {
            mattertags: false,
            sweeps: false,
        };

        const screenShotUri = await mpSdk.current?.Renderer.takeScreenShot(resolution, visibility);
        if (screenShotUri) {
            download(screenShotUri, 'screenshot.jpg', '../image');
        }
    }, []);

    const onToolbarUpdate = useCallback((key: Tool, value?: View | number) => {
        switch (key) {
            case 'info':
                toggleToolbarInfo();
                break;
            case 'position':
                toggleToolbarPostion();
                break;
            case 'highlights':
                toggleToolbarHighlights();
                break;
            case 'tour':
                toggleToolbarTour();
                break;
            case 'view':
                changeView(value as View);
                break;
            case 'floors':
                if (!(value === 0 && isInsideRef.current)) {
                    changeFloor(value as number);
                }
                break;
            case 'music':
                if (toolMusic && musicSrc) {
                    if (prevMusicActive.current) {
                        prevMusicActive.current = false;
                        togglePlayingMusicSrc(false);
                    } else {
                        prevMusicActive.current = true;
                        togglePlayingMusicSrc(true);
                    }
                }
                toggleToolbarMusic();
                break;
            case 'share':
                setShareUrl(window.location.href.split('?')[0]);
                toggleShareModal();
                break;
            case 'screenshot':
                takeScreenShot();
                break;
            case 'getLocation':
                logSweepData();
                break;
            default:
                break;
        }
    }, []);

    const onIframeLoad = useCallback(async () => {
        await connectSdk();
        await registerIcon();
        handleMatterTag();
        handleFloors();
        handleModelState();
        handleViewChange();
        handlePose();

        iframe.current?.contentDocument
            ?.getElementsByTagName('body')[0]
            .addEventListener('click', blurSideUI);
    }, []);

    const onClickTour = useCallback(
        (tourKey: string) => {
            const { modelId: prevModelId } = tourData[activeTour];
            const { modelId, sr, ss, sweepId, rotation } = tourData[tourKey];

            if (modelId && sweepId) {
                if (modelId === prevModelId) {
                    mpSdk.current?.Sweep.moveTo(sweepId, { rotation });
                } else {
                    setAppPhase(undefined);

                    const nextQueryStr = getEmbedQueryStr({ modelId, sr, ss });
                    setEmbededQueryStr(nextQueryStr);
                }
            }

            setActiveTour(tourKey);
            onClickTourFn(tourData[tourKey]);
            toggleMenu(false);
        },
        [activeTour],
    );

    const onToggleSideUI = useCallback(() => {
        toggleToolbarHighlights(false);

        if (audioActive) {
            toggleAudio(false);
        } else if (imageSlideActive) {
            toggleImageSlide(false);
            toggleInfoDetail(true);
        } else if (!infoDetailActive) {
            toggleMenu();
        } else {
            toggleInfoDetail(false);
        }
    }, [audioActive, infoDetailActive]);

    useLayoutEffect(() => {
        prevWidthRef.current = width;
        prevHeightRef.current = height;

        return () => {
            mpSdk.current?.disconnect();

            iframe.current?.contentDocument
                ?.getElementsByTagName('body')[0]
                .removeEventListener('click', blurSideUI);

            if (sideUIOpeningTimeout.current) {
                window.clearTimeout(sideUIOpeningTimeout.current);
            }

            if (checkMovingTimeout.current) {
                window.clearTimeout(checkMovingTimeout.current);
            }

            if (openDetailCheckTimeout.current) {
                window.clearTimeout(openDetailCheckTimeout.current);
            }

            if (openDetailTimeout.current) {
                window.clearTimeout(openDetailTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        nowWidthRef.current = width;
        nowHeightRef.current = height;
    }, [width, height]);

    useEffect(() => {
        if (appPhase === mpSdk.current?.App.Phase.PLAYING) {
            getTourData();
        }
    }, [appPhase]);

    useEffect(() => {
        if (appPhase === mpSdk.current?.App.Phase.PLAYING) {
            mpSdk.current?.Settings.update('features/mattertags', toolbarInfoActive);
        }
    }, [appPhase, toolbarInfoActive]);

    useEffect(() => {
        if (appPhase === mpSdk.current?.App.Phase.PLAYING) {
            mpSdk.current?.Settings.update('features/sweep_pucks', toolbarPostiionActive);
        }
    }, [appPhase, toolbarPostiionActive]);

    useEffect(() => {
        if (appPhase === mpSdk.current?.App.Phase.PLAYING) {
            setInitPos();
        }
    }, [appPhase]);

    useEffect(() => {
        toolbarTourPlayingRef.current = toolbarTourPlaying;

        if (toolbarTourPlaying) {
            mpSdk.current?.Tour.start();
        } else {
            mpSdk.current?.Tour.stop();
        }
    }, [toolbarTourPlaying]);

    useEffect(() => {
        isInsideRef.current = isInside;

        Object.keys(tagsCollection.current).forEach((id) => {
            mpSdk.current?.Mattertag.preventAction(id, {
                opening: true,
                navigating: isInside,
            });
        });
    }, [isInside]);

    const logoRef = useRef(null);

    const { media, mediaScale } = useMedia();
    const hasNotch = useHasNotch();

    useEffect(() => {
        let y = 0;

        if (audioActive) {
            y = media === 'phone' ? 124 : 60;
        }

        const tween = gsap.to(logoRef.current, { y: y * mediaScale });

        return () => {
            tween.kill();
        };
    }, [audioActive, media, mediaScale]);

    return (
        <div className={classnames(styles.matterportShowcase, className)}>
            <iframe
                ref={iframe}
                onLoad={onIframeLoad}
                title="matterport showcase"
                src={`/bundle/showcase.html?${embededQueryStr}`}
                frameBorder="0"
                allowFullScreen={false}
                allow="xr-spatial-tracking"
            />

            <div
                ref={logoRef}
                className={classnames(
                    styles.logo,
                    audioActive && styles.audioActive,
                    // isChrome && styles.chrome,
                    !isChrome && hasNotch && styles.hasNotch,
                )}
            >
                {logo && (
                    <div className={styles.logoImgWrapper}>
                        <img src={logo} alt="logo" />
                    </div>
                )}
                {/* <div className={styles.title}>
                    <span>|</span>
                    <span>{tourData[activeTour].path}</span>
                    <span>{tourData[activeTour].text.split(' ')[1]}</span>
                </div> */}
            </div>

            <div className={styles.modelController}>
                <Toolbar
                    className={classnames(
                        styles.toolbar,
                        toolbarHighlightsOpen && styles.highlightsActive,
                        // isChrome && styles.chrome,
                        !isChrome && hasNotch && styles.hasNotch,
                    )}
                    info={toolInfo}
                    position={toolPosition}
                    highlights={{ enable: toolHighlight, value: toolbarHighlightsOpen }}
                    tour={{ enable: toolTour, value: toolbarTourPlaying }}
                    view={toolView}
                    floors={{
                        enable: toolFloors && floorsData.length > 1,
                        value: 1,
                        data: {
                            options: isInside
                                ? floorsData
                                : [
                                      ...floorsData,
                                      {
                                          label: 'All',
                                          value: 0,
                                      },
                                  ],
                        },
                    }}
                    music={{ enable: !!musicSrc, value: toolbarMusicActive }}
                    share={toolShare}
                    screenshot={toolScreenshot}
                    getLocation={toolGetLocation}
                    onClick={() => {
                        blurSideUI(true);
                    }}
                    onStateUpdate={onToolbarUpdate}
                />
                <HighlightSlider
                    className={styles.highlights}
                    active={toolbarHighlightsOpen}
                    highlights={highlightData}
                    onClick={onClickHighlight}
                />
            </div>
            <div className={styles.sideUi}>
                {menu && (
                    <Menu
                        className={styles.menu}
                        active={menuActive}
                        intro={intro}
                        fixedLink={fixedLink}
                        navLinks={
                            tourList.length > 1
                                ? tourList.map((key) => {
                                      const { text } = tourData[key];

                                      return {
                                          text,
                                          value: key,
                                      };
                                  })
                                : []
                        }
                        onClickNav={onClickTour}
                    />
                )}
                <InfoDetail
                    active={infoDetailActive}
                    fixedLink={fixedLink}
                    label={infoLabel}
                    intro={infoDescription}
                    images={infoImages}
                    imageType={infoImageType}
                    imageAlign={infoImageAlign}
                    onClickImage={() => {
                        toggleImageSlide(true);
                        toggleInfoDetail(false);
                    }}
                />
                {imageSlideActive && <ImageSlider images={infoImages} />}
                {videoActive && (
                    <Video
                        src={videoSrc}
                        onClickOutside={() => {
                            toggleVideo(false);
                        }}
                    />
                )}
                {(audioOpen || audioActive) && (
                    <Audio
                        active={audioActive}
                        trackName={audioLabel}
                        src={audioSrc}
                        onCloseComplete={() => {
                            toggleAudioOpen(false);
                        }}
                    />
                )}
                {shareModalActive && (
                    <ShareModal
                        url={shareUrl}
                        onClose={() => {
                            toggleShareModal(false);
                            setShareUrl('');
                        }}
                        onCheckPos={(checked) => {
                            if (checked) {
                                setPosShareUrl();
                            } else {
                                setShareUrl(window.location.href.split('?')[0]);
                            }
                        }}
                    />
                )}
                <Icon
                    className={styles.toggleBtn}
                    active={menuActive || infoDetailActive || imageSlideActive || audioActive}
                    icon={{ default: menuIcon, hover: menuIcon }}
                    activeIcon={{ default: closeIcon, hover: closeIcon }}
                    onClick={onToggleSideUI}
                />
            </div>

            <Transition active={appPhase !== 'appphase.playing'} />

            {startIntroActive && (
                <Intro
                    backgroundImage={tourData[activeTour].cover}
                    backgroundImageMobile={tourData[activeTour].coverMobile}
                    logo={logo}
                    slogan="ENJOY THE EXHIBITION"
                    onClickSkip={() => {
                        if (musicSrc) {
                            togglePlayingMusicSrc(true);
                            prevMusicActive.current = true;
                        }
                    }}
                    onComplete={onEnterMain}
                    loading={appPhase !== 'appphase.playing'}
                />
            )}
        </div>
    );
};

export default MatterportShowcase;
